
import React, { useState, useRef } from 'react';
import { ParkingSpace, Infringement } from '../types';
import { ICONS } from '../constants';

interface ComplaintFormProps {
  spaces: ParkingSpace[];
  onReport: (complaint: Omit<Infringement, 'id' | 'status' | 'reportedAt' | 'reportedBy'>) => void;
  onCancel: () => void;
}

const ComplaintForm: React.FC<ComplaintFormProps> = ({ spaces, onReport, onCancel }) => {
  const [spaceId, setSpaceId] = useState<number | ''>('');
  const [reason, setReason] = useState<Infringement['reason']>('UNAUTHORIZED_VEHICLE');
  const [description, setDescription] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhoto(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const space = spaces.find(s => s.id === spaceId);
    onReport({
      spaceId: spaceId === '' ? undefined : spaceId,
      spaceLabel: space?.displayLabel || 'General Site',
      reason,
      description,
      photoData: photo || undefined
    });
  };

  return (
    <div className="max-w-2xl mx-auto text-black">
      <div className="bg-white p-10 rounded-[2.5rem] border border-slate-200 shadow-xl">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-rose-100 rounded-2xl flex items-center justify-center text-rose-600">
            <ICONS.AlertCircle className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-xl font-black text-slate-900 tracking-tight">Report a Conflict</h3>
            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Direct alert to site administration</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Affected Parking Space</label>
            <select 
              value={spaceId} 
              onChange={e => setSpaceId(e.target.value === '' ? '' : parseInt(e.target.value))}
              className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl font-bold text-sm outline-none focus:border-accent-red focus:bg-white transition-all text-black"
            >
              <option value="">General Site Issue / Unknown Space</option>
              {spaces.map(s => <option key={s.id} value={s.id}>{s.displayLabel}</option>)}
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Issue Category</label>
            <select 
              value={reason} 
              onChange={e => setReason(e.target.value as Infringement['reason'])}
              className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl font-bold text-sm outline-none focus:border-accent-red focus:bg-white transition-all text-black"
            >
              <option value="UNAUTHORIZED_VEHICLE">Unauthorized Vehicle</option>
              <option value="BLOCKING_ACCESS">Blocking Access/Aisle</option>
              <option value="OVERSTAY">Overstay / Late Exit</option>
              <option value="EV_ISSUE">EV Issue</option>
              <option value="OTHER">Other Conflict</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Incident Details</label>
            <textarea 
              value={description} 
              onChange={e => setDescription(e.target.value)}
              placeholder="Please provide vehicle registration if applicable..."
              className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-50 rounded-2xl font-bold text-sm outline-none focus:border-accent-red focus:bg-white transition-all h-32 text-black"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Visual Evidence (Photo)</label>
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-slate-200 rounded-2xl aspect-video flex flex-col items-center justify-center cursor-pointer hover:border-accent-red hover:bg-red-50/20 transition-all overflow-hidden relative"
            >
              {photo ? (
                <img src={photo} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <>
                  <ICONS.Zap className="w-8 h-8 text-slate-300 mb-2" />
                  <p className="text-[10px] font-black uppercase text-slate-400">Click to upload image</p>
                </>
              )}
              <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button 
              type="button" 
              onClick={onCancel}
              className="flex-1 py-4 px-6 rounded-xl text-xs font-black uppercase tracking-widest text-slate-400 hover:text-slate-600 transition-all"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="flex-1 py-4 px-6 bg-accent-red text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-xl shadow-red-100 hover:bg-black transition-all"
            >
              Submit Report
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ComplaintForm;
